import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import WordListWord from "./WordListWord";
import { setPuzzleAndPuzzleType } from "Store/features/puzzleSlice";
import { makeSavePuzzleRequest } from "API/Puzzles";

import "./WordSearch.css";

// [0, 1]: Right (move horizontally to the right)
// [1, 0]: Down (move vertically downwards)
// [0, -1]: Left (move horizontally to the left)
// [-1, 0]: Up (move vertically upwards)
const directions = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

const WordSearch = (props) => {
  const { onClickBack, wordsInput, puzzleTitle } = props;
  const dispatch = useDispatch();


  const [draftWords, setDraftWords] = useState(wordsInput)
  const puzzleSlice = useSelector((state) => state.puzzleSlice);
  const puzzleId = useSelector((state) => state.puzzleSlice?.puzzleId);
  const authToken = useSelector((state) => state.authSlice?.authToken);
  
  const puzzle = useSelector((state) => state.puzzleSlice?.puzzle);
  const words = useSelector((state) => state.puzzleSlice?.puzzle?.words) || draftWords;

  const grid = useSelector((state) => state.puzzleSlice?.puzzle?.grid);
  const wordLocations = useSelector(
    (state) => state.puzzleSlice?.puzzle?.wordLocations
  );
  const selectedWord = useSelector(
    (state) => state.puzzleSlice?.puzzle?.selectedWord
  );
  const selectedCoordinates = useSelector(
    (state) => state.puzzleSlice?.puzzle?.selectedCoordinates
  );
  const handleSaveWordSearch = useCallback(async () => {
    const draftPuzzle = {
      ...puzzle,
      words,
    };
    const savePuzzleParam = {
      title: puzzleTitle,
      id: puzzleId,
      puzzle: draftPuzzle,
      puzzleType: "wordsearch",
    };
    await makeSavePuzzleRequest(dispatch, authToken, savePuzzleParam);
  }, [dispatch, authToken, puzzle, puzzleId, puzzleTitle, words]);
  // Should this just be state?
  const size = useSelector((state) => state.puzzleSlice?.puzzle?.size || 10);

  const setSize = useCallback((size) => {
    dispatch(
      setPuzzleAndPuzzleType({
        ...puzzleSlice,
        puzzle: {
          ...puzzle,
          size,
        },
      })
    );
  }, []);

  useEffect(() => {
    console.log({ grid });
  }, [grid]);

  useEffect(() => {
    if (!selectedWord) return;
    let draftSelectedCoordinates = [];
    let { row, col, dir, word } = selectedWord;

    for (let i = 1; i <= word?.length; i++) {
      draftSelectedCoordinates = [...draftSelectedCoordinates, { row, col }];
      row += dir[0];
      col += dir[1];
    }
    dispatch(
      setPuzzleAndPuzzleType({
        ...puzzleSlice,
        puzzle: {
          ...puzzle,
          selectedCoordinates: draftSelectedCoordinates,
        },
      })
    );
    // setSelectedCoordinates(draftSelectedCoordinates);
  }, [selectedWord]);

  const isSelected = useCallback(
    (i, j) => {
      return selectedCoordinates?.some((cord) => {
        return cord.row === i && cord.col === j;
      });
    },
    [selectedCoordinates]
  );
  const generateGrid = useCallback(
    (words, size) => {
      let localGrid = Array(size)
        .fill()
        .map(() => Array(size).fill(""));

      let localWordLocations = {};

      const placeWord = (word) => {
        const len = word.length;
        let placed = false;
        let attempts = 0;
        while (!placed) {
          if (attempts > 1000) {
            console.log("Failed to place word", word);
            setSize(size + 1);
            return null;
          }
          const dir = directions[Math.floor(Math.random() * directions.length)];
          const row = Math.floor(Math.random() * size);
          const col = Math.floor(Math.random() * size);
          attempts += 1;

          if (canPlaceWord(word, row, col, dir, localGrid, size)) {
            for (let i = 0; i < len; i++) {
              localGrid[row + i * dir[0]][col + i * dir[1]] = word[i];
            }
            // console.log({ word, row, col, dir });
            // console.log(localWordLocations);
            localWordLocations = {
              ...localWordLocations,
              [word]: { row, col, dir, word },
            };
            placed = true;
          }
          if (attempts > 1000) {
            setSize(size + 1);
            return null;
          }
        }
        dispatch(
          setPuzzleAndPuzzleType({
            ...puzzleSlice,
            puzzle: {
              ...puzzle,
              words: (wordsInput || puzzle.words)?.map((word) =>
                word.toUpperCase()
              ),
              wordLocations: localWordLocations,
            },
          })
        );
        return word;
      };

      const canPlaceWord = (word, row, col, dir, localGrid, size) => {
        for (let i = 0; i < word.length; i++) {
          const newRow = row + i * dir[0];
          const newCol = col + i * dir[1];

          // Check if the word fits within the grid bounds
          if (newRow < 0 || newRow >= size || newCol < 0 || newCol >= size) {
            return false;
          }

          // Check if the current cell is either empty or contains the matching letter
          if (
            localGrid[newRow][newCol] !== "" &&
            localGrid[newRow][newCol] !== word[i]
          ) {
            return false;
          }

          // Check surrounding cells for invalid adjacencies
          const prevRow = newRow - dir[0];
          const prevCol = newCol - dir[1];
          const nextRow = newRow + dir[0];
          const nextCol = newCol + dir[1];

          // Ensure the cell before the first letter and after the last letter in the word are empty or out of bounds
          if (
            i === 0 &&
            prevRow >= 0 &&
            prevRow < size &&
            prevCol >= 0 &&
            prevCol < size
          ) {
            if (localGrid[prevRow][prevCol] !== "") return false;
          }
          if (
            i === word.length - 1 &&
            nextRow >= 0 &&
            nextRow < size &&
            nextCol >= 0 &&
            nextCol < size
          ) {
            if (localGrid[nextRow][nextCol] !== "") return false;
          }

          // Ensure that adjacent cells perpendicular to the word's direction are empty
          const perpendicularDir = dir[0] === 0 ? [1, 0] : [0, 1];
          const adjRowLeft = newRow - perpendicularDir[0];
          const adjColLeft = newCol - perpendicularDir[1];
          const adjRowRight = newRow + perpendicularDir[0];
          const adjColRight = newCol + perpendicularDir[1];

          if (
            (adjRowLeft >= 0 &&
              adjRowLeft < size &&
              adjColLeft >= 0 &&
              adjColLeft < size &&
              localGrid[adjRowLeft][adjColLeft] !== "") ||
            (adjRowRight >= 0 &&
              adjRowRight < size &&
              adjColRight >= 0 &&
              adjColRight < size &&
              localGrid[adjRowRight][adjColRight] !== "")
          ) {
            return false;
          }
        }
        return true;
      };

      const placedWord = words?.map((word) => placeWord(word)).filter(word => word);
      setDraftWords(placedWord)

      for (let i = 0; i < size; i++) {
        for (let j = 0; j < size; j++) {
          if (localGrid[i][j] === "") {
            localGrid[i][j] = String.fromCharCode(
              65 + Math.floor(Math.random() * 26)
            );
          }
        }
      }

      return localGrid;
    },
    [size, grid, words]
  );
  useEffect(() => {
    dispatch(
      setPuzzleAndPuzzleType({
        ...puzzleSlice,
        puzzle: {
          ...puzzle,
          grid: generateGrid(wordsInput, Number(size)),
        },
      })
    );
  }, [wordsInput, size]);

  const handleUpdateWordsearchSize = useCallback(
    (size) => {
      setSize(size);
    },
    [size]
  );

  const handleClickWordListWord = useCallback(
    (word) => {
      const wordLocation = wordLocations[word];
      dispatch(
        setPuzzleAndPuzzleType({
          ...puzzleSlice,
          puzzle: {
            ...puzzle,
            selectedWord: wordLocation,
          },
        })
      );
    },
    [wordLocations, dispatch]
  );

  const handleSetWords = useCallback(
    (words) => {
      const capsWords = words?.map((word) => word.toUpperCase());
      dispatch(
        setPuzzleAndPuzzleType({
          ...puzzleSlice,
          puzzle: {
            ...puzzle,
            words: capsWords,
          },
        })
      );
    },
    [wordLocations]
  );

  const handleRegenerateWordsearch = useCallback(() => {
    dispatch(
      setPuzzleAndPuzzleType({
        ...puzzleSlice,
        puzzle: {
          ...puzzle,
          grid: generateGrid(wordsInput, size),
        },
      })
    );
  }, [wordsInput, size]);

  return (
    <>
      <div className="create-games-topbar-container">
        <div className="create-games-topbar-back-button" onClick={onClickBack}>
          <b>{"< "}</b>Back
        </div>
        <div
          className="create-games-topbar-regenerate-button"
          onClick={handleRegenerateWordsearch}
        >
          Regenerate
        </div>
        <div
          className="create-games-topbar-continue-button"
          name='save-puzzle'
          onClick={handleSaveWordSearch}
        >
          Save<b>{" >"}</b>
        </div>
      </div>
      <div className="word-search-editor-container">
        <div className="word-search-editor-content-container">
          <div className="word-search">
            {grid?.map((row, rowIndex) => (
              <div id={rowIndex} key={rowIndex} className="wordsearch-grid-row">
                {row?.map((letter, cellIndex) => (
                  <div
                    key={cellIndex}
                    id={cellIndex}
                    className={`wordsearch-grid-cell ${
                      isSelected(rowIndex, cellIndex) ? "selected" : ""
                    }`}
                  >
                    {letter}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {wordsInput?.length ? (
            <div className="word-search-game-word-header-container">
              {wordsInput?.map((w) => (
                <WordListWord word={w} onClick={handleClickWordListWord} editor={true} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default WordSearch;
